<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { toFixedNumber } from '@/shared/utils'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import CyberSecurityModalQC from '@/entities/Flow/components/CyberSecurityModalQC/CyberSecurityModalQC.vue'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import ToggleQC from '@qicgroupdoha/qic-connect-ds/components/ToggleQC'

  interface Props {
    title?: string
    description?: string
    variant: 'covid' | 'winter-sports' | 'cyber'
    isPriceLoading?: boolean
    price?: number
    travelers?: any[]
    quoteId?: number
  }

  interface Emits {
    (e: 'toggle-winter-sports', isChecked: boolean, srNo: string): void
    (e: 'add-cyber-cover'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    title: 'Covers Block',
    description: 'Covers Block Description',
    isPriceLoading: false,
    price: 0,
    travelers: () => [],
    quoteId: undefined,
  })
  const emit = defineEmits<Emits>()
  const isActive = defineModel<boolean>('isActive', { default: false })

  const isCyberSecurityModalOpen = ref(false)

  const { t } = useI18n()
  const gaService = useGoogleAnalytics()
  const route = useRoute()

  const textPriceDescription = computed(() => {
    if (props.variant === 'cyber') {
      const holder = props.travelers.find((traveler) => traveler.srNo === 'holder')
      return holder
        ? t('travel.outbound.cyber_security_addon.price_desc.text', {
            firstName: holder?.firstName,
            secondName: holder?.lastName,
          })
        : ''
    } else if (props.variant === 'covid' && props.travelers.length > 1) {
      return t('travel.outbound.covid.price_desc.text', { count: props.travelers.length })
    }
    return ''
  })

  const openCyberSecurityModal = () => {
    if (props.quoteId) {
      gaService.sendClickLearnMore(route.name as AvailableFlowRoutes, {
        quote_id: props.quoteId,
        state: isActive.value,
        cover_name: 'cyber_security',
      })
    } else {
      gaService.sendClickLearnMore(route.name as AvailableFlowRoutes, {
        cover_name: 'cyber_security',
      })
    }

    gaService.sendCyberPopupOpened(route.name as AvailableFlowRoutes, props.quoteId)

    isCyberSecurityModalOpen.value = true
  }
</script>

<template>
  <FlowCardQC class="addons-block-qc">
    <WrapperQC direction="column">
      <WrapperQC direction="column" gap="var(--spacing-2-5)" class="addons-block-qc__header">
        <h2 class="addons-block-qc__title heading-5">{{ title }}</h2>
        <p class="addons-block-qc__description text-regular">
          {{ description }}
          <span
            v-if="variant === 'cyber'"
            class="addons-block-qc__learn-more text-regular"
            data-test="learn-more-cyber-security-addon"
            @click="openCyberSecurityModal"
            >{{ $t('outbound.addon.learn_more') }}</span
          >
        </p>
      </WrapperQC>

      <WrapperQC
        v-if="variant === 'cyber' || variant === 'covid'"
        gap="var(--spacing-3)"
        class="addons-block-qc__footer"
      >
        <WrapperQC direction="column" gap="var(--spacing-2)" class="start-content">
          <h3 class="addons-block-qc__price paragraph-default-bold ltr">
            {{ $t('qar') }} {{ toFixedNumber(price) }}
          </h3>
          <p class="addons-block-qc__price-description text-small-regular">
            {{ textPriceDescription }}
          </p>
        </WrapperQC>
        <ToggleQC
          v-model="isActive"
          :data-test="`addon-toggle-${variant}`"
          :loading="isPriceLoading"
          size="medium"
        />
      </WrapperQC>

      <WrapperQC
        v-else-if="variant === 'winter-sports'"
        direction="column"
        class="addons-block-qc__winter-sports"
      >
        <WrapperQC
          v-for="(traveler, i) in travelers"
          :key="i"
          gap="var(--spacing-3)"
          class="addons-block-qc__winter-sports-item"
        >
          <WrapperQC direction="column" gap="var(--spacing-2)" class="start-content">
            <h3 class="addons-block-qc__price paragraph-default-bold ltr">
              {{ $t('qar') }} {{ toFixedNumber(price) }}
            </h3>
            <p class="addons-block-qc__price-description text-small-regular">
              {{
                `${$t('travel.outbound.winter_sport.price_desc.text')} ${traveler.firstName} ${traveler.lastName}`
              }}
            </p>
          </WrapperQC>
          <ToggleQC
            v-model="traveler.winSports"
            :data-test="`winter-sports-toggle-${i}`"
            :loading="isPriceLoading"
            size="medium"
            @click="
              $event.target.value &&
              emit('toggle-winter-sports', $event.target.checked, traveler.srNo)
            "
          />
        </WrapperQC>
      </WrapperQC>
    </WrapperQC>
  </FlowCardQC>

  <CyberSecurityModalQC
    v-if="isCyberSecurityModalOpen"
    is-add-button-shown
    :quote-id="quoteId"
    :cyber-state="isActive"
    @add-cyber-cover="emit('add-cyber-cover')"
    @close="isCyberSecurityModalOpen = false"
  />
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/mixins';

  .addons-block-qc {
    @include mixins.mobile-flow {
      padding: var(--spacing-8) 0;
    }
  }

  .addons-block-qc__header {
    border-bottom: 1px solid var(--colorBrdrSrfSecondary);
    padding-bottom: var(--spacing-7);

    @include mixins.mobile-flow {
      padding-inline: var(--spacing-5);
    }
  }

  .addons-block-qc__footer {
    justify-content: space-between;
    align-items: center;
    padding-top: var(--spacing-6);

    @include mixins.mobile-flow {
      padding-inline: var(--spacing-5);
    }
  }

  .addons-block-qc__winter-sports {
    padding-top: var(--spacing-2);

    @include mixins.mobile-flow {
      padding-inline: var(--spacing-5);
    }
  }

  .addons-block-qc__winter-sports-item {
    justify-content: space-between;
    align-items: center;
    padding-block: var(--spacing-6);
    border-bottom: 1px solid var(--colorBrdrSrfSecondary);

    &:last-child {
      border-bottom: none;
      padding-block-end: 0;
    }
  }

  .addons-block-qc__description,
  .addons-block-qc__price-description {
    color: var(--colorTxtSrfSecondary);
  }

  .addons-block-qc__learn-more {
    display: block;
    color: var(--colorTxtSrfAction);
    cursor: pointer;
  }

  .start-content {
    align-items: start;
  }
</style>
